import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AccrualEntryListRow,
  AccrualEntryStatus,
  AccrualTemplate,
  AccrualTemplateListRow,
  CreateAccrualTemplate,
  ReverseAccruals,
  ReverseAccrualsResult,
  RunAccruals,
  RunAccrualsResult,
  UpdateAccrualTemplate,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache-key scopes — keyed by companyId per the convention used in
// queries/lva.ts and queries/ar.ts.
const templatesKey = (companyId: string | undefined) => ['accrual-templates', companyId] as const;
const entriesKey = (companyId: string | undefined) => ['accrual-entries', companyId] as const;

export interface AccrualTemplateFilters {
  branchId?: string;
  isActive?: boolean;
}

export function useAccrualTemplates(
  companyId: string | undefined,
  filters: AccrualTemplateFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.isActive !== undefined) qs.set('isActive', String(filters.isActive));
  const search = qs.toString();
  return useQuery({
    queryKey: [...templatesKey(companyId), filters],
    queryFn: () =>
      api<AccrualTemplateListRow[]>(
        `/companies/${companyId}/accrual-templates${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCreateAccrualTemplate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAccrualTemplate) =>
      apiJson<CreateAccrualTemplate, AccrualTemplate>(
        'POST',
        `/companies/${companyId}/accrual-templates`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: templatesKey(companyId) }),
  });
}

export function useUpdateAccrualTemplate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAccrualTemplate }) =>
      apiJson<UpdateAccrualTemplate, AccrualTemplate>(
        'PATCH',
        `/companies/${companyId}/accrual-templates/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: templatesKey(companyId) }),
  });
}

export interface AccrualEntryFilters {
  period?: string;
  templateId?: string;
  status?: AccrualEntryStatus;
}

export function useAccrualEntries(
  companyId: string | undefined,
  filters: AccrualEntryFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.templateId) qs.set('templateId', filters.templateId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...entriesKey(companyId), filters],
    queryFn: () =>
      api<AccrualEntryListRow[]>(
        `/companies/${companyId}/accruals/entries${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useRunAccruals(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RunAccruals) =>
      apiJson<RunAccruals, RunAccrualsResult>('POST', `/companies/${companyId}/accruals/run`, data),
    onSuccess: () => {
      // The run creates rows + posts a JV — invalidate both scopes so the
      // matrix view and any GL drilldowns refresh.
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useReverseAccruals(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReverseAccruals) =>
      apiJson<ReverseAccruals, ReverseAccrualsResult>(
        'POST',
        `/companies/${companyId}/accruals/reverse`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}
